import partymax from './party_max.json'
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { Municipality } from '@ta-interaktiv/react-municipality-search'
import ChoroplethMap from './choroplethMap'
import styled from 'styled-components'
import * as d3 from 'd3'
import { useWindowSize } from 'usehooks-ts'
import { party_colors } from 'components/MainComponent/party_colors'
import {
  ContentWidth,
  TextWidthExtended,
} from '@ta-interaktiv/react-disco-grid'
import { MunicipalityDisplay } from './MunicipalityDisplay'
import { UserSelection } from 'components/UserSelection'

const party_max = partymax as { [key: string]: { [key: number]: number } }
export interface Props {
  /** The key of the property you want to compare over the municipalities (e.g. "taxBurden") */
  propertyNameToCompare?: string
  /** If the property we are comparing is an array we need a index to what value we want to compare it in te array */
  indexOfPropertyToCompare?: number
  /** Hide the search bar */
  hideMunicipalitySearch?: boolean
  /** The lowest value of your municipalityData  */
  minValue?: number
  /** The highest value of your municipalityData  */
  maxValue?: number
  /** The dataset used for the municipality search bar. Go to the s3 bucket: interaktiv-tenant-source/static/gemeindesuche/ to see the available datasets (e.g. "2020") */
  municipalitySearchYear?: string
  /** The placeholder of the municipality search bar default is 'Gemeindesuche' and 'Nom de la commune' */
  municipalitySearchPlaceholder?: string
  /** Pass a custom array of municipalities for the municipality search */
  municipalitySearchCustomMunicipalities?: Municipality[]
  /** The year of the topoJson that will be fetched and used to draw the map. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJsonYear: number
  /** If you want to use your own (local) topoJson you can pass it here. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJson?: any

  /** This function gets triggered when the mouse hovers over a municipality. The municipalityData object of the currently hovered municipality will be passed to the function (e.g. {bfsId: 261, taxBurden: "7"}) */
  hoverHandler?: (newData: any) => void
  /** show the zoom controls */
  isZoomable?: boolean
  /** zoom to the currently selected municipality */
  zoomIntoActiveMunicipality?: boolean
  /** disable touch events on 600px width or smaller */
  disableTouchOnMobile?: boolean
  /** To show a tooltip when hovering you need to pass a tooltip component. The municipalityData object of the currently hovered municipality will be passed as a prop (e.g. {bfsId: 261, taxBurden: "7"}) */
  ToolTip?: FC<ToolTipProps>

  /** stroke-width of active municipality */
  activeStrokeWidth?: string
  /** stroke-color of active municipality */
  activeStrokeColor?: string

  /** fill of all lakes */
  lakeFillColor?: string
  /** stroke-color of all lakes */
  lakeStrokeColor?: string
  /** stroke-width of all lakes */
  lakeStrokeWidth?: string
  /** stroke-color national border */
  nationalBorderColor?: string
  /** stroke-width national border */
  nationalBorderWidth?: string
  /** stroke-color of all cantons */
  cantonStrokeColor?: string
  /** stroke-width of all cantons */
  cantonStrokeWidth?: string
  /** lang of component */
  locale?: 'de' | 'fr'
  /** custom props for this story */
  // currentName: string
  /** event handler for fullscreen state change */
  fullscreenChangeHandler?: (isFullscreen: boolean) => void
  /** is the map in fullscreen mode or not */
  isFullscreen?: boolean
  /** custom legend component */
  LegendComponent?: any
  /** scroll map into view when new municipality is selected */
  scrollIntoView?: boolean
  sort?: 'rel' | 'abs'

  /////////
  /////////
  /////////
  /////////
  onPartyChange?: (party: string) => void
  onYearChange?: (year: number) => void
  onBfsIdChange?: (bfsId: number) => void
  layerStyles?: LayerStyles
  activeMunicipalityId: number | undefined
  setActiveMunicipalityId: (bfsId: number | undefined) => void
  activeParty: string
  activeYear: number
  setActiveData?: (e: any) => void
  colorMode?: 'light' | 'dark' | undefined
  setActiveYear: (year: number) => void
  setActiveParty: (party: string) => void
}

export const SwissMap: FC<Props> = ({
  propertyNameToCompare,
  indexOfPropertyToCompare,
  hideMunicipalitySearch = false,
  minValue,
  maxValue,
  municipalitySearchYear,
  municipalitySearchPlaceholder,
  topoJsonYear = 2019,
  topoJson,
  hoverHandler,
  isZoomable = true,
  zoomIntoActiveMunicipality = true,
  disableTouchOnMobile,
  ToolTip,
  activeStrokeWidth = '2',
  activeStrokeColor = '#A757A3',
  onPartyChange,
  onYearChange,
  locale = 'de',
  fullscreenChangeHandler,
  isFullscreen,
  LegendComponent,
  scrollIntoView,
  sort = 'rel',
  layerStyles,
  onBfsIdChange,
  activeMunicipalityId = 0,
  setActiveMunicipalityId,
  activeParty = 'Schweiz',
  activeYear = 2019,
  setActiveData,
  colorMode = 'light',
  setActiveYear,
  setActiveParty,
}): ReactElement => {
  const [toolTipData, setToolTipData] = useState<any | null>()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const toolTipRef = React.useRef<HTMLDivElement>(null)
  const staticRef = React.useRef<HTMLDivElement>(null)
  const [activeData, setActiveMuniData] = useState<any>()
  const [yearSelectopen, setYearSelectOpen] = useState<boolean>(false)
  const [partySelectOpen, setPartySelectionOpen] = useState<boolean>(false)

  // const [currentYear, setCurrentYear] = useState<number>(activeYear)
  // const [activeMunicipalityId, setActiveMunicipalityId] = useState<number>()
  const [currentData, setCurrenData] = useState<any | undefined>()
  const [loadedData, setLoadedData] = useState<{ [key: string]: any }>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const toolTipStyle = {
    pointerEvents: 'none' as 'none',
    position: 'fixed' as 'fixed',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    zIndex: 502,
  }

  useEffect(() => {
    setIsLoading(true)
    if (loadedData[activeYear]) {
      setCurrenData(loadedData[activeYear])
      setIsLoading(false)

      return
    }
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2023/nationalratswahlen23/data_municipality/by_year_lps/' +
        activeYear +
        '.json?a=' +
        Math.random()
    )
      .then((response) => response.json())
      .then((data) => {
        const preped = prepLoadedData(data)
        setLoadedData({ ...loadedData, [activeYear]: preped })

        // This is a quick and dirty fix
        // I have been asked to add the Mitte party on every election year
        // The Mitte party is the merge of BDP and CVP in 2021
        preped.map((m: any) => {
          const res = m.results
          const bpd = parseFloat(res['BDP']) || 0
          const cvp = parseFloat(res['CVP']) || 0

          if (activeYear < 2023) res["Mitte"] = bpd + cvp

          delete res.BDP
          delete res.CVP

          const maxKey = Object.keys(res).reduce((a, b) => {
            const aValue = parseFloat(res[a]) || -Infinity
            const bValue = parseFloat(res[b]) || -Infinity
            return aValue > bValue ? a : b
          })

          m.strongest = [maxKey, parseFloat(res[maxKey])]
        })
        // End of quick and dirty fix

        setCurrenData(preped)
        setIsLoading(false)
      })
  }, [activeYear])

  var baseScale = [-1, 0, 10, 20, 50, 100]
  var max = activeParty === 'Schweiz' ? 100 : party_max[activeParty][2023]
  var scaleSteps =
    activeParty === 'Schweiz' ? baseScale : getScaleSteps(baseScale, max)

  let color_linear = d3
    .scaleQuantile<string, number>()
    .domain(scaleSteps)
    .range([
      party_colors['andere'] + '11',
      party_colors['andere'] + '33',
      party_colors['andere'] + '66',
      party_colors['andere'] + '99',
      party_colors['andere'] + 'cc',
      party_colors['andere'],
    ])

  const color_categorical = d3
    .scaleOrdinal()
    .domain(Object.keys(party_colors))
    .range(Object.values(party_colors))

  const colorMuni = useMemo(
    () => (e: any) => {
      if (activeParty !== 'Schweiz') {
        color_linear.range([
          party_colors[activeParty] + '11',
          party_colors[activeParty] + '33',
          party_colors[activeParty] + '66',
          party_colors[activeParty] + '99',
          party_colors[activeParty] + 'cc',
          party_colors[activeParty],
        ])

        const col = color_linear(e.results[activeParty]) as string
        return col as d3.RGBColor | d3.HSLColor | string
      } else {
        if (e.strongest[1] == '.') return 'url(#diagonalHatch)'
        color_linear.range([
          party_colors['andere'] + '33',
          party_colors['andere'] + '55',
          party_colors['andere'] + '88',
          party_colors['andere'] + 'bb',
          party_colors['andere'] + 'dd',
          party_colors['andere'],
        ])
        const opacity = d3
          .scaleQuantile<number, number>()
          .domain(scaleSteps)
          .range([0, 20, 50, 70, 100])

        const col = d3.color(color_categorical(e.strongest[0]) as string)
        return col?.copy({ opacity: opacity(e.strongest[1]) / 100 }) as
          | d3.RGBColor
          | d3.HSLColor
          | string
      }
    },
    [activeParty, color_linear, color_categorical]
  )
  useEffect(() => {}, [colorMuni])
  function prepLoadedData(rawdata: any) {
    return rawdata.map((muni: any) => {
      muni.bfsId = muni.id
      muni.viz = muni.results.SP
      return muni
    })
    // .filter((f: any) => f.viz !== '.')
  }

  function newActiveMunicipalityIdHandler(muniId: number) {
    if (onBfsIdChange) onBfsIdChange(muniId)
    setActiveMunicipalityId(+(muniId + ''))
  }
  function handleHover(newData: any) {
    if (ToolTip) {
      setToolTipData(newData)
    }
  }

  useEffect(() => {
    if (!currentData) return
    if (!activeMunicipalityId) activeMunicipalityId = 0
    var d =
      currentData.find(
        (m: any) =>
          +m.bfsId === activeMunicipalityId || m.bfsId === activeMunicipalityId
      ) || undefined
    setActiveMuniData(d)
    setActiveData && setActiveData(d)
    // staticRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [activeMunicipalityId, currentData])

  function clearSelectedMunicipality() {
    setActiveMunicipalityId(undefined)
    setActiveData && setActiveData(undefined)
  }
  return (
    <>
      <ContentWidth>
        <MapWrapper
          className='swiss-map'
          style={{
            zIndex: 401,
          }}
        >
          {LegendComponent && (
            <LegendComponent
              colorScale={color_linear}
              colorMode={colorMode}
              values={[...Array(color_linear.domain().length).keys()]
                .slice(1)
                .map((i) => {
                  const size =
                    i > 0
                      ? color_linear.domain()[i] - color_linear.domain()[i - 1]
                      : 0
                  return {
                    label: color_linear.domain()[i],
                    color: colorMuni({
                      results: {
                        [activeParty !== 'Schweiz' ? activeParty : 'neutral']:
                          color_linear.domain()[i - 1],
                      },
                      strongest: [
                        activeParty !== 'Schweiz' ? activeParty : 'neutral',
                        color_linear.domain()[i - 1],
                      ],
                    }),
                    size,
                  }
                })}
            />
          )}
          {currentData && (
            <ChoroplethMap
              onZoomOut={() => !isMobile && setActiveMunicipalityId(undefined)}
              layerStyles={layerStyles}
              municipalityData={currentData}
              propertyNameToCompare={'viz'}
              indexOfPropertyToCompare={indexOfPropertyToCompare}
              minValue={minValue}
              maxValue={maxValue}
              year={topoJsonYear}
              topoJson={topoJson}
              activeMunicipalityId={activeMunicipalityId}
              newActiveMunicipalityIdHandler={newActiveMunicipalityIdHandler}
              color={colorMuni}
              hoverHandler={handleHover}
              isZoomable={isZoomable}
              zoomIntoActiveMunicipality={true}
              disableTouchOnMobile={disableTouchOnMobile}
              activeStrokeWidth={activeStrokeWidth}
              activeStrokeColor={activeStrokeColor}
              toolTipRef={toolTipRef}
              municipalityFallbackFillColor='url(#diagonalHatch)'
              // fullscreenChangeHandler={fullscreenChangeHandler}
              isFullscreen={isFullscreen}
              fullscreenHint={locale === 'fr' ? 'Plein écran' : 'Vollbild'}
              scrollIntoView={scrollIntoView}
            />
          )}
          {isLoading && <Loading>Lade Daten</Loading>}
          {ToolTip && !isMobile && toolTipData && (
            <div id='tooltip' style={toolTipStyle} ref={toolTipRef}>
              <MunicipalityDisplay data={toolTipData} width={300} />
            </div>
          )}
          {!!(activeData && isMobile && activeMunicipalityId) && (
            <div
              ref={staticRef}
              style={{
                zIndex: 500,
                position: 'absolute',
                top: '-5px',
                left: 0,
              }}
            >
              <MunicipalityDisplay
                data={activeData}
                isStatic
                closeable
                clearSelectedMunicipality={clearSelectedMunicipality}
              />
            </div>
          )}
        </MapWrapper>
      </ContentWidth>

      <UserSelection
        colorMode={colorMode}
        locale={locale}
        activeParty={activeParty}
        activeYear={activeYear}
        handlePartyChange={setActiveParty}
        handleYearChange={setActiveYear}
        handleMunicipalityChange={newActiveMunicipalityIdHandler}
      />
      <TextWidthExtended>
        <>
          {!!(activeData && !isMobile) && (
            <div ref={staticRef}>
              <MunicipalityDisplay data={activeData} isStatic />
            </div>
          )}
        </>
      </TextWidthExtended>
    </>
  )
}
const Loading = styled.div`
  background-color: var(--site-background);
  padding: 15px;
  border-radius: 5px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const MapWrapper = styled.div`
  transition: margin-bottom 0.3s ease-in-out;
  padding-bottom: 25px;
  position: relative;
  /* z-index: 400; */
  .tooltip {
    z-index: 600;
  }
`
const Center = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 30px;
  /* margin-top: 30px; */
  position: relative;
  margin-top: -10px;
`

function getScaleSteps(basesteps: number[], maxvalue: number) {
  var steps = [
    -1,
    0,
    ...basesteps.slice(2).map((s) => {
      return (s * maxvalue) / basesteps[basesteps.length - 1]
    }),
  ]
  return steps
}
